import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'astroturf/react';
// @ts-ignore
import StickySidebar from 'sticky-sidebar';
import 'resize-sensor';

import routes from 'javascripts/core_pre_webpack/routes';
import { currentUser } from 'js/utils/user';
import { CurrentUser } from 'js/core_post_webpack/models/user';
import { fetchReaderMessageLimit } from 'js/core_webpack/sitewide_business_elements/download/reader_message_form.wjs.jsx';
import { getBulkFollowListForWork } from 'js/social/api/user_following_api';
import SnackBar from 'js/social/shared/SnackBar';

import {
  downloadWork as downloadWorkDefault,
  messageAuthor as messageAuthorDefault,
  report as reportDefault,
} from './actions';
import { useAuthors as useAuthorsDefault } from './hooks/useAuthors';
import { useRelatedWorks as useRelatedWorksDefault } from './hooks/useRelatedWorks';
import { useAuthorWorks as useAuthorWorksDefault } from './hooks/useAuthorWorks';
import { useRis as useRisDefault } from './hooks/useRis';
import { useRoute } from './hooks/useRoute';
import AuthorsCard from './AuthorsCard';
import AuthorMoreWorksCard from './AuthorMoreWorksCard';
import Bookmarkers from './Bookmarkers';
import { BulkFollowCard, BulkFollowCardData } from './BulkFollowCard';
import Loading from './Loading';
import RecommendationsCard, { Recommendation } from './RecommendationsCard';
import RelatedWorksCard from './RelatedWorksCard';
import ResearchInterestsCard from './ResearchInterestsCard';
import { Author, ResearchInterest, Work } from './hooks/types';

// The string values of this enum are the keys of the data
// that's sent down from the server, ie:
// GET /v0/works/1234567/liswp_sidebar
//
// {
//   authors: [],
//   relatedWorks: [],
// }

export enum Card {
  BulkFollowCard = 'bulkFollowCard',
  Authors = 'authors',
  Bookmarkers = 'bookmarkers',
  AuthorWorks = 'authorWorks',
  Mentions = 'mentions',
  Recommendations = 'recommendations',
  RelatedWorks = 'relatedWorks',
  ResearchInterests = 'researchInterests',
  RecommendedForYou = 'recommendedForYou',
}

export type Props = {
  allow?: Card[];
  apiOptions: Record<string, string>;
  // The apiRoute takes in a string instead of the actual
  // route function so that the Sidebar can be rendered in
  // slim templates using ReactOnRails
  apiRoute: keyof typeof routes;
  downloadWork?: typeof downloadWorkDefault;
  messageAuthor?: typeof messageAuthorDefault;
  report?: typeof reportDefault;
  // Whether to show the Sidebar at mobile size (<992px)
  showMobile?: boolean;
  useAuthors?: typeof useAuthorsDefault;
  useRelatedWorks?: typeof useRelatedWorksDefault;
  useAuthorWorks?: typeof useAuthorWorksDefault;
  useRis?: typeof useRisDefault;
  workId?: number | string;
  sticky?: boolean;
};

// The shape of the data that's sent down from the server, ie:
// GET /v0/works/1234567/liswp_sidebar
type ServerData = {
  [Card.BulkFollowCard]?: BulkFollowCardData;
  [Card.Authors]?: Author[];
  [Card.ResearchInterests]?: ResearchInterest[];
  [Card.RelatedWorks]?: Work[];
  [Card.AuthorWorks]?: {
    count: number;
    url: string;
    works: Work[];
  };
  [Card.Recommendations]?: Recommendation[];
  [Card.RecommendedForYou]?: Work[];
  [Card.Bookmarkers]?: boolean;
  [Card.Mentions]?: never;
};

// Default is to allow all types of cards
// If you create a new card type, you should add it here
// The order of cards here is the same order that they
// will be shown
const allowDefault = [
  Card.BulkFollowCard, // For wolf_q1_2024_swp_bulk_follow_sidebar A/B test
  Card.Authors,
  Card.Bookmarkers,
  Card.AuthorWorks,
  Card.Mentions,
  Card.Recommendations,
  Card.RelatedWorks,
  Card.ResearchInterests,
  Card.RecommendedForYou,
];

function getUserRec(currentUser: CurrentUser, comment: string | undefined) {
  return {
    comment: comment || '',
    date: new Date().toString(),
    user: {
      displayName: currentUser.display_name || '',
      percentile: 0,
      // Url for the user's avatar
      photo: currentUser.photo,
      position: (currentUser.department && currentUser.department.name) || '',
      university:
        (currentUser.department &&
          currentUser.department.university &&
          currentUser.department.university.name) ||
        '',
    },
  };
}

const Container = styled.aside<{ showMobile?: boolean }>`
  background: #fff;
  // The 2px y offset is so the box-shadow doesn't show up
  // over the header at the top of the page
  box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.25);
  width: 460px;

  // For sticky-sidebar performance
  transform: translate3d(0, 0, 0);
  will-change: position, transform;

  @media (max-width: 1200px) {
    width: 395px;
  }

  @media (max-width: 992px) {
    display: none;
  }

  &.showMobile {
    @media (max-width: 992px) {
      display: block;
      max-width: unset;
      width: 100%;
    }
  }
`;

// All functions and hooks used are overrideable.
// For instance, if you want to disallow users from messaging authors
// when not logged in, you can pass in a custom function on the
// `messageAuthor` prop that checks if the user is logged in first.
// The default implementation of `messageAuthor` and the rest of the actions are
// in actions.ts, so you can import and call it in your custom function if you need to.
//
// Other functionality is encapsulated custom hooks, like the `useAuthors` hook.
// These are also overrideable, but you'll have to make sure to fulfill the
// same interface as the default implementation.
// `useAuthors` for example, is expected to return an object with
// properties `followAuthor`, `authors`, and `setAuthors`.
// The default implementations of all hooks are exported from hooks/index.ts,
// you can call them inside your own implementation if necessary.
const Sidebar: React.FC<Props> = ({
  allow = allowDefault,
  apiOptions,
  apiRoute,
  downloadWork = downloadWorkDefault,
  messageAuthor = messageAuthorDefault,
  report = reportDefault,
  showMobile,
  useAuthors = useAuthorsDefault,
  useRelatedWorks = useRelatedWorksDefault,
  useAuthorWorks = useAuthorWorksDefault,
  useRis = useRisDefault,
  workId,
  sticky = true,
}: Props) => {
  /* Hardcoded to true because we were fuzzy with boundaries for what was part of an AB test.
   * Not all usages of altLook-tested components were in the test. Manually finding which
   * components those were, and then splitting them up, was too much work.
   * https://github.com/academia-edu/academia-app/pull/22841#discussion_r628554304
   */
  const altLook = true;

  const {
    data: serverData,
    error,
    loading,
  } = useRoute<ServerData>(routes[apiRoute], apiOptions, null, [apiOptions]);

  // These are empty at first, populated by the sidebar api call
  const { followAuthor, authors, setAuthors } = useAuthors([]);
  const { bookmarkWork, followWorkAuthor, relatedWorks, setRelatedWorks } =
    useRelatedWorks([]);
  const { followRi, ris, setRis } = useRis([]);
  const {
    bookmarkAuthorWork,
    followAuthorInAuthorWorks,
    authorWorks,
    setAuthorWorks,
  } = useAuthorWorks(null);
  const [bulkFollowCard, setBulkFollowCard] =
    useState<BulkFollowCardData | null>(null);
  const [showConfirmationSnackbar, setShowConfirmationSnackbar] =
    useState(false);

  useEffect(() => {
    if (workId) {
      fetchBulkFollowCardData(setBulkFollowCard, Number(workId));
    }
  }, [workId]);

  // For wolf_q1_2024_swp_bulk_follow_sidebar A/B test
  // Display the confirmation snackbar in the designated div
  useEffect(() => {
    if (showConfirmationSnackbar) {
      const containingDiv = document.getElementById('snackbar-div');
      const snackbar = (
        <SnackBar
          message={
            <>
              <span style={{ textDecoration: 'underline' }}>View updates</span>
              &nbsp;from academics you're now following
            </>
          }
          type="success"
          onClick={() => {
            window.location.href = routes.home_url();
          }}
          durationInMs={5000}
          showIcon={false}
        />
      );
      if (containingDiv) {
        ReactDOM.render(snackbar, containingDiv);
      }
    }
  }, [showConfirmationSnackbar]);

  const [certificationData, setCertificationData] =
    useState<Recommendation | null>(null);

  const [readerMessageLimitReached, setReaderMessageLimitReached] =
    useState<boolean>(true);

  useEffect(() => {
    window.addEventListener(
      'certificationComplete',
      (e) => {
        // @ts-ignore
        setCertificationData(getUserRec(currentUser, e.detail.content));
      },
      { once: true },
    );

    if (workId) {
      fetchReaderMessageLimitReached(
        setReaderMessageLimitReached,
        workId.toString(),
      );
    }
  }, [workId, setReaderMessageLimitReached]);
  // This effect updates the hooks that manage their own state
  // (for things like following an author) when the api returns data
  useEffect(() => {
    if (!serverData) return;

    // Note: BulkFollowCard data is fetched separately using a different endpoint
    // that is also used by the TopCard
    const {
      [Card.Authors]: authors,
      [Card.ResearchInterests]: ris,
      [Card.RelatedWorks]: relatedWorks,
      [Card.AuthorWorks]: authorWorks,
      [Card.RecommendedForYou]: recommendedForYou,
    } = serverData;

    if (authors) {
      setAuthors(authors);
    }
    if (ris) {
      setRis(ris);
    }
    if (relatedWorks) {
      setRelatedWorks(relatedWorks);
    } else if (recommendedForYou) {
      setRelatedWorks(recommendedForYou);
    }
    if (authorWorks && authors?.[0]) {
      setAuthorWorks(
        authors[0].displayName,
        authorWorks.works,
        authorWorks.count,
        authorWorks.url,
      );
    }
  }, [serverData, setAuthors, setRis, setRelatedWorks, setAuthorWorks]);

  const stickySidebar = useRef();
  // I wasn't able to get this working with a useRef and useEffect,
  // the element was never defined in the useEffect,
  // and it wouldn't get called again when the ref was updated.
  const containerRef = useCallback(
    (el: HTMLElement) => {
      if (!sticky) return;
      // The sidebar itself is rendered via React on Rails,
      // so it has a wrapping div.
      // In order for sticky-sidebar to work, the sidebar el and the content el
      // have to be siblings inside the same container
      const sidebarContainer = el?.parentElement;
      if (!sidebarContainer) return;

      stickySidebar.current = new StickySidebar(sidebarContainer, {
        containerSelector: '.work-body-wrapper',
        innerWrapperSelector: '.js-sticky-sidebar',
        // This tells sticky-sidebar to use ResizeSensor.js, which is imported at the top.
        // This allows sticky-sidebar to work after resizes
        resizeSensor: true,
        // This is added to the wrapping div created by React on Rails.
        // The wrapping div doesn't have a set width by default,
        // which messes up sticky-sidebar.
        // This class has styles to fix it.
        stickyClass: 'sticky-sidebar-container',
      });
    },
    [sticky],
  );

  // Manually call `StickySidebar#updateSticky` when scribd is ready
  // The scribd doc loading changes the container's height,
  // which stickySidebar needs to know about.
  useEffect(() => {
    const listener = () => {
      // @ts-ignore
      stickySidebar.current?.updateSticky();
    };

    // This is fired from responsive_scribd_view when scribd is ready
    // so we can manually call `updateSticky` for the sidebar.
    // resizeSensor alone does not do the trick,
    // if the scribd doc loads after the sidebar
    window.addEventListener('scribdDocReady', listener);

    return () => window.removeEventListener('scribdDocReady', listener);
  }, []);

  if (error) return null;

  if (loading || serverData == null) {
    return <Loading showMobile={showMobile} />;
  }

  if (!routes[apiRoute]) {
    // eslint-disable-next-line no-console
    console.warn(`Received unknown apiRoute ${apiRoute}, not rendering.`);
    return null;
  }

  return (
    <Container
      className="js-sticky-sidebar"
      ref={containerRef}
      showMobile={showMobile}
    >
      {allow.map((type) => {
        if (
          !serverData[type] &&
          type !== Card.Recommendations &&
          type !== Card.BulkFollowCard
        ) {
          return null;
        }

        switch (type) {
          case Card.BulkFollowCard:
            if (bulkFollowCard) {
              return (
                <BulkFollowCard
                  key="bulk-follow-card"
                  altLook={altLook}
                  onClose={(followingsAdded) => {
                    if (followingsAdded) {
                      setShowConfirmationSnackbar(true);
                    }
                  }}
                  {...bulkFollowCard}
                />
              );
            }
            return null;
          case Card.Authors:
            return (
              <AuthorsCard
                altLook={altLook}
                authors={authors}
                key={type}
                onFollow={(author) =>
                  followAuthor(author, 'single_work_author')
                }
                onMessage={messageAuthor}
              />
            );
          case Card.AuthorWorks:
            return (
              authorWorks && (
                <AuthorMoreWorksCard
                  altLook={altLook}
                  author={authorWorks.author}
                  count={authorWorks.count}
                  works={authorWorks.works}
                  key={type}
                  onDownload={downloadWork(
                    readerMessageLimitReached,
                    setReaderMessageLimitReached,
                  )}
                  onReport={report}
                  onMessage={messageAuthor}
                  onViewAll={() => {
                    window.location.href = authorWorks.url;
                  }}
                  onFollow={followAuthorInAuthorWorks}
                  onSave={bookmarkAuthorWork}
                  impressionSource="author-more-works"
                />
              )
            );
          case Card.Bookmarkers:
            if (!workId) return null;
            return <Bookmarkers workId={workId} />;
          case Card.Mentions:
            // TODO: Add mentions card
            return null;
          case Card.Recommendations: {
            const recData = [
              certificationData,
              serverData[Card.Recommendations],
            ]
              .flat()
              .filter((element) => element);
            return (
              <RecommendationsCard
                altLook={altLook}
                key={type}
                // @ts-ignore
                recommendations={recData}
              />
            );
          }
          case Card.RelatedWorks:
            return (
              <RelatedWorksCard
                altLook={altLook}
                works={relatedWorks}
                key={type}
                onDownload={downloadWork(
                  readerMessageLimitReached,
                  setReaderMessageLimitReached,
                )}
                onFollow={followWorkAuthor}
                onMessage={messageAuthor}
                onReport={report}
                onSave={bookmarkWork}
                sourceWorkId={workId}
                impressionSource="related-works-sidebar"
              />
            );
          case Card.RecommendedForYou:
            return (
              <RelatedWorksCard
                altLook={altLook}
                headerText={'Recommended for you'}
                works={relatedWorks}
                key={type}
                onDownload={downloadWork(
                  readerMessageLimitReached,
                  setReaderMessageLimitReached,
                )}
                onFollow={followWorkAuthor}
                onMessage={messageAuthor}
                onReport={report}
                onSave={bookmarkWork}
                sourceWorkId={workId}
                impressionSource="rec4u-sidebar"
              />
            );
          case Card.ResearchInterests:
            return (
              <ResearchInterestsCard
                altLook={altLook}
                key={type}
                onFollow={followRi}
                researchInterests={ris}
              />
            );
          default:
            // eslint-disable-next-line no-console
            console.warn(`Received unknown card type ${type}, not rendering.`);
            return null;
        }
      })}
    </Container>
  );
};

const fetchReaderMessageLimitReached = async (
  setReaderMessageLimitReached: (value: boolean) => void,
  workId: string,
) => {
  const readerMessageLimitReachedResult = await fetchReaderMessageLimit(workId);
  setReaderMessageLimitReached(readerMessageLimitReachedResult);
};

const fetchBulkFollowCardData = async (
  setBulkFollowCard: (value: BulkFollowCardData) => void,
  workId: number,
) => {
  setBulkFollowCard(await getBulkFollowListForWork(workId));
};

export default Sidebar;
